import { Table as BootstrapTable } from 'react-bootstrap';
import { useTable, usePagination } from 'react-table';
import { useEffect, useRef } from 'react';

import styles from './Table.module.css';

export default function Table ({
    columns,
    data,
    onRowClick,
    selected,
    className,
    rowsPerPage: _rowsPerPage,
    pageSkipping = false,
    updatePageSize = false,
    showSelectionIndicator = false,
    tableLayout = 'auto',
    resetPageKey,
    isLoading = false,
    emptyMessage = 'No records found.'
}) {
    const prevDataLengthRef = useRef(data.length);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: _rowsPerPage || 10 },
            autoResetPage: false
        },
        usePagination
    );

    // Reset to page 1 when resetPageKey changes (e.g., when switching programs)
    useEffect(() => {
        if (resetPageKey !== undefined) {
            gotoPage(0);
        }
    }, [resetPageKey, gotoPage]);

    // Also reset if data length changes significantly (indicating completely different data)
    useEffect(() => {
        const currentLength = data.length;
        const prevLength = prevDataLengthRef.current;

        // Reset page if data length changed by more than 50% (likely a different dataset)
        if (prevLength > 0 && Math.abs(currentLength - prevLength) / prevLength > 0.5) {
            gotoPage(0);
        }

        prevDataLengthRef.current = currentLength;
    }, [data.length, gotoPage]);

    const isEmpty = data.length === 0;
    const columnCount = columns.length + (showSelectionIndicator ? 1 : 0);

    return (
        <>
            <div className={styles.tableContainer}>
                <BootstrapTable
                    {...getTableProps()}
                    className={className}
                    style={{
                        tableLayout: tableLayout,
                        margin: 0,
                        width: '100%'
                    }}
                >
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {showSelectionIndicator && (
                                    <th className={`${styles.indicatorCell} ${styles.tableHeaderCell}`}></th>
                                )}
                                {headerGroup.headers.map(column => {
                                    const { render, getHeaderProps } = column;
                                    return (
                                        <th
                                            {...getHeaderProps()}
                                            className={styles.tableHeaderCell}
                                        >
                                            {render('Header')}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()} className={styles.tableBody}>
                        {isEmpty && (
                            <tr className={styles.emptyRow}>
                                <td
                                    colSpan={columnCount}
                                    className={styles.emptyCell}
                                    aria-busy={isLoading || undefined}
                                >
                                    {isLoading ? 'Loading…' : emptyMessage}
                                </td>
                            </tr>
                        )}
                        {page.map((row, i) => {
                            prepareRow(row);
                            const isHighlighted = Array.isArray(selected)
                                ? selected.some(sel => sel._id === row.original._id)
                                : row.original === selected;
                            return (
                                <tr
                                    {...row.getRowProps()}
                                    onClick={() => onRowClick && onRowClick(row.original)}
                                    className={
                                        showSelectionIndicator
                                            ? styles.row
                                            : `${styles.row} ${isHighlighted ? styles.selected : ''}`
                                    }
                                >
                                    {showSelectionIndicator && (
                                        <td className={styles.indicatorCell}>
                                            <span
                                                className={styles.selectionIndicator}
                                                style={{
                                                    background: isHighlighted ? 'var(--color-accent)' : 'var(--color-border)',
                                                }}
                                            />
                                        </td>
                                    )}
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </BootstrapTable>
            </div>
            {!isEmpty && pageOptions.length > 1 && (
                <div className={styles.footer}>
                    <div className={styles.pagination}>
                        <button
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                            className={styles.paginationButton}
                            aria-label="Go to first page"
                        >
                            {'<<'}
                        </button>
                        <button
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                            className={styles.paginationButton}
                            aria-label="Go to previous page"
                        >
                            {'<'}
                        </button>
                        <span className={styles.paginationInfo}>
                            Page:
                            <strong>
                                {pageIndex + 1} of {pageOptions.length}
                            </strong>
                        </span>
                        <button
                            onClick={() => nextPage()}
                            disabled={!canNextPage}
                            className={styles.paginationButton}
                            aria-label="Go to next page"
                        >
                            {'>'}
                        </button>
                        <button
                            onClick={() => gotoPage(pageCount - 1)}
                            disabled={!canNextPage}
                            className={styles.paginationButton}
                            aria-label="Go to last page"
                        >
                            {'>>'}
                        </button>
                        {pageSkipping && (
                            <span className={styles.pageSkipping}>
                                | Go to page:
                                <input
                                    type='number'
                                    defaultValue={pageIndex + 1}
                                    onChange={e => {
                                        const page = e.target.value
                                            ? Number(e.target.value) - 1
                                            : 0;
                                        gotoPage(page);
                                    }}
                                    className={styles.pageInput}
                                    min="1"
                                    max={pageOptions.length}
                                />
                            </span>
                        )}
                        {updatePageSize && (
                            <select
                                value={pageSize}
                                onChange={e => {
                                    setPageSize(Number(e.target.value));
                                }}
                                className={styles.pageSizeSelect}
                            >
                                {[10, 20, 30, 40, 50].map(pageSize => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
